import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SummaryCard from './SummaryCard';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { partService, truckService } from '../api/api';
import { getVehicleCategoryId } from '../utils/vehicleCategories';
import { formatPortalDate } from '../utils/dateFormat';
import { currentPartPlacement, isPurchaseShelfPart } from '../utils/partLocation.mjs';

function numericValue(value, fallback = 0) {
  const parsed = parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : fallback;
}

function partLabel(part) {
  return part?.part_type || part?.sku || `Part ${part?.id}`;
}
//Location Column Header
function inventoryLocationLabels(part) {
  const rawLocations = Array.isArray(part?.source_locations) && part.source_locations.length
    ? part.source_locations
    : String(part?.location || '')
      .split('+')
      .map((value) => value.trim())
      .filter(Boolean);

  const uniqueLocations = Array.from(new Set(rawLocations.map((value) => String(value || '').trim()).filter(Boolean)));
  return uniqueLocations.length ? uniqueLocations : ['Shelf'];
}
function sourcePartsFor(part) {
  return Array.isArray(part?.source_parts) && part.source_parts.length ? part.source_parts : [part];
}

function purchaseShelfSourceParts(part) {
  return sourcePartsFor(part).filter(isPurchaseShelfPart);
}

function partsTruckSourceParts(part) {
  return sourcePartsFor(part).filter((sourcePart) => currentPartPlacement(sourcePart).truckId);
}

//Shelf Location Column Header
function shelfLocationText(part) {
  const shelfLocations = purchaseShelfSourceParts(part)
    .map((sourcePart) => String(sourcePart?.location || '').trim())
    .filter(Boolean);

  if (shelfLocations.length) return Array.from(new Set(shelfLocations)).join(', ');

  const directLocation = String(part?.location || '').trim();
  if (isPurchaseShelfPart(part)) return directLocation;
  return '—';
}

function hasPurchaseShelfInventory(part) {
  if (typeof part?.has_purchase_shelf_inventory === 'boolean') return part.has_purchase_shelf_inventory;
  return purchaseShelfSourceParts(part).length > 0;
}

function purchaseShelfQuantity(part) {
  if (part?.purchase_shelf_quantity !== null && part?.purchase_shelf_quantity !== undefined) {
    return Number(part.purchase_shelf_quantity || 0);
  }

  const shelfParts = purchaseShelfSourceParts(part);
  if (shelfParts.length) {
    return shelfParts.reduce((sum, sourcePart) => sum + Number(sourcePart?.quantity || 0), 0);
  }

  return isPurchaseShelfPart(part) ? Number(part?.quantity || 0) : 0;
}

function partsTruckQuantity(part) {
  if (part?.parts_truck_quantity !== null && part?.parts_truck_quantity !== undefined) {
    return Number(part.parts_truck_quantity || 0);
  }

  const truckParts = partsTruckSourceParts(part);
  if (truckParts.length) {
    return truckParts.reduce((sum, sourcePart) => sum + Number(sourcePart?.quantity || 0), 0);
  }

  return 0;
}

function hasPartsTruckInventory(part) {
  return partsTruckSourceParts(part).length > 0
    || partsTruckQuantity(part) > 0;
}

function isPurchaseNeeded(part, threshold) {
  return hasPurchaseShelfInventory(part) && purchaseShelfQuantity(part) < Number(threshold ?? part?.low_stock_threshold ?? 2);
}

function highlightPartIdFromHash() {
  const hash = window.location.hash || '';
  const queryStart = hash.indexOf('?');
  if (queryStart < 0) return null;
  const params = new URLSearchParams(hash.slice(queryStart + 1));
  const value = params.get('highlightPart');
  return value ? Number(value) : null;
}

function inventoryLocationChipSx(label) {
  const normalizedLabel = String(label || '').trim().toLowerCase();
  const palette = normalizedLabel === 'shelf'
    ? { bg: '#e5f9eb', color: '#166534', border: '#22c55e55' }
    : normalizedLabel === 'solvent shed'
      ? { bg: '#f3e8ff', color: '#7e22ce', border: '#c084fc66' }
      : normalizedLabel === 'shop'
        ? { bg: '#ffedd5', color: '#9a3412', border: '#fb923c66' }
        : { bg: '#e4f0ff', color: '#075985', border: '#0284c755' };
  return {
    bgcolor: palette.bg,
    color: palette.color,
    border: `1px solid ${palette.border}`,
    fontWeight: 900,
    '& .MuiChip-icon': { color: palette.color },
  };
}

export default function InventoryManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [parts, setParts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [assignDialog, setAssignDialog] = useState({ open: false, part: null, vehicleId: '', quantity: 1 });
  const [detailPart, setDetailPart] = useState(null);
  const [detailPhotos, setDetailPhotos] = useState([]);
  const [detailPhotosLoading, setDetailPhotosLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [inventoryFilter, setInventoryFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [highlightPartId, setHighlightPartId] = useState(() => highlightPartIdFromHash());

  const loadParts = async () => {
    try {
      setLoading(true);
      const [partRes, vehicleRes] = await Promise.all([
        partService.getInventory(),
        truckService.getAll({ includeArchived: 1 }),
      ]);
      const inventoryRows = Array.isArray(partRes.data) ? partRes.data : [];
      const vehicleRows = vehicleRes.data?.data || vehicleRes.data || [];
      setVehicles((Array.isArray(vehicleRows) ? vehicleRows : []).filter((vehicle) => (
        vehicle.archived !== true
        && getVehicleCategoryId(vehicle) !== 'partsTruck'
      )));
      setParts(inventoryRows);
      setDrafts(Object.fromEntries(inventoryRows.map((part) => [
        part.id,
        {
          quantity: part.quantity ?? 0,
          low_stock_threshold: part.low_stock_threshold ?? 2,
          low_stock_email_enabled: part.low_stock_email_enabled === true,
          low_stock_email_recipients: part.low_stock_email_recipients || '',
        },
      ])));
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to load inventory.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadParts();
  }, []);

  useEffect(() => {
    const refresh = () => loadParts();
    const updateHighlight = (event) => {
      const nextId = Number(event.detail?.partId || highlightPartIdFromHash() || 0) || null;
      setSearch('');
      setInventoryFilter('all');
      setHighlightPartId(nextId);
    };
    window.addEventListener('partsUpdated', refresh);
    window.addEventListener('dataUpdated', refresh);
    window.addEventListener('biofleet:openInventoryPart', updateHighlight);
    window.addEventListener('hashchange', updateHighlight);
    return () => {
      window.removeEventListener('partsUpdated', refresh);
      window.removeEventListener('dataUpdated', refresh);
      window.removeEventListener('biofleet:openInventoryPart', updateHighlight);
      window.removeEventListener('hashchange', updateHighlight);
    };
  }, []);

  useEffect(() => {
    if (!highlightPartId || loading || parts.length === 0) return;
    const timer = window.setTimeout(() => {
      document.getElementById(`inventory-part-${highlightPartId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [highlightPartId, loading, parts.length]);

  const filteredParts = useMemo(() => {
    const query = search.trim().toLowerCase();
    const byStatus = parts.filter((part) => {
      const quantity = Number(part.quantity ?? 0);
      const threshold = Number(part.low_stock_threshold ?? 2);
      if (inventoryFilter === 'inStock') return quantity > 0;
      if (inventoryFilter === 'lowStock') return isPurchaseNeeded(part, threshold);
      if (inventoryFilter === 'outOfStock') return quantity <= 0;
      if (inventoryFilter === 'shelfStock') return hasPurchaseShelfInventory(part);
      if (inventoryFilter === 'totalInventory') return true;
      if (inventoryFilter === 'lowShelf') return isPurchaseNeeded(part, threshold);
      if (inventoryFilter === 'outShelf') return hasPurchaseShelfInventory(part) && purchaseShelfQuantity(part) <= 0;
      if (inventoryFilter === 'outPartsTruck') return hasPartsTruckInventory(part) && partsTruckQuantity(part) <= 0;
      return true;
    });
    if (!query) return byStatus;
    return byStatus.filter((part) => [
      part.sku,
      part.part_type,
      part.part_number_oem,
      part.serial_number,
      part.location,
      part.make,
      part.model,
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query)));
  }, [parts, search, inventoryFilter]);

  const paginatedParts = useMemo(
    () => filteredParts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredParts, page, rowsPerPage],
  );

  useEffect(() => {
    setPage(0);
  }, [search, inventoryFilter]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredParts.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredParts.length, page, rowsPerPage]);

  useEffect(() => {
    if (!highlightPartId) return;
    const index = filteredParts.findIndex((part) => Number(part.representative_id || part.id) === highlightPartId);
    if (index >= 0) setPage(Math.floor(index / rowsPerPage));
  }, [filteredParts, highlightPartId, rowsPerPage]);

  const stats = useMemo(() => {
    return parts.reduce((acc, part) => {
      const quantity = Number(part.quantity ?? 0);
      const threshold = Number(part.low_stock_threshold ?? 2);
      const shelfQuantity = purchaseShelfQuantity(part);
      const truckQuantity = partsTruckQuantity(part);
      const hasShelf = hasPurchaseShelfInventory(part);
      const hasTruck = hasPartsTruckInventory(part);
      acc.totalParts += 1;
      acc.totalQuantity += quantity;
      if (hasShelf) acc.shelfStockItems += 1;
      acc.totalInventoryQuantity += quantity;
      if (hasShelf && shelfQuantity < threshold) acc.lowShelfStock += 1;
      if (hasShelf && shelfQuantity <= 0) acc.outShelfStock += 1;
      if (hasTruck && truckQuantity <= 0) acc.outPartsTruckStock += 1;
      if (isPurchaseNeeded(part, threshold)) acc.lowStock += 1;
      if (quantity <= 0) acc.outOfStock += 1;
      return acc;
    }, {
      totalParts: 0,
      totalQuantity: 0,
      shelfStockItems: 0,
      totalInventoryQuantity: 0,
      lowShelfStock: 0,
      outShelfStock: 0,
      outPartsTruckStock: 0,
      lowStock: 0,
      outOfStock: 0,
    });
  }, [parts]);

  const activeInventoryFilterLabel = {
    all: 'All stock items',
    inStock: 'Items with stock',
    lowStock: 'Low stock',
    outOfStock: 'Out of stock',
    shelfStock: 'Stock items on shelf',
    totalInventory: 'Total inventory',
    lowShelf: 'Low stock parts on shelf',
    outShelf: 'Out of stock parts on shelf',
    outPartsTruck: 'Out of stock parts on parts truck',
  }[inventoryFilter] || 'All stock items';

  const updateDraft = (partId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [partId]: {
        quantity: prev[partId]?.quantity ?? 0,
        low_stock_threshold: prev[partId]?.low_stock_threshold ?? 2,
        low_stock_email_enabled: prev[partId]?.low_stock_email_enabled ?? false,
        low_stock_email_recipients: prev[partId]?.low_stock_email_recipients ?? '',
        [field]: value,
      },
    }));
  };

  const savePart = async (part) => {
    const draft = drafts[part.id] || {};
    const quantity = numericValue(draft.quantity, part.quantity ?? 0);
    const threshold = numericValue(draft.low_stock_threshold, part.low_stock_threshold ?? 2);
    const emailEnabled = draft.low_stock_email_enabled === true;
    const emailRecipients = draft.low_stock_email_recipients || '';

    try {
      setSavingId(part.id);
      const sourceParts = Array.isArray(part.source_parts) && part.source_parts.length ? part.source_parts : [part];
      const currentTotal = sourceParts.reduce((sum, item) => sum + Number(item.quantity ?? 0), 0);
      let remainingQuantity = quantity;
      const updates = [];

      if (quantity >= currentTotal) {
        const delta = quantity - currentTotal;
        updates.push(...sourceParts.map((item, index) => ({
          ...item,
          quantity: Number(item.quantity ?? 0) + (index === 0 ? delta : 0),
        })));
      } else {
        updates.push(...sourceParts.map((item) => {
          const nextQuantity = Math.min(Number(item.quantity ?? 0), remainingQuantity);
          remainingQuantity -= nextQuantity;
          return { ...item, quantity: nextQuantity };
        }));
      }

      await Promise.all(updates.map((item) => partService.update(item.id, {
        quantity: item.quantity,
        low_stock_threshold: threshold,
        low_stock_email_enabled: emailEnabled,
        low_stock_email_recipients: emailEnabled ? emailRecipients : '',
      })));
      await loadParts();
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      setSnackbar({ open: true, severity: 'success', message: `${partLabel(part)} inventory updated.` });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to update inventory.',
      });
    } finally {
      setSavingId(null);
    }
  };

  const adjustQuantity = (part, delta) => {
    const current = numericValue(drafts[part.id]?.quantity, part.quantity ?? 0);
    updateDraft(part.id, 'quantity', Math.max(0, current + delta));
  };

  const getVehicleLabel = (vehicle) => [
    vehicle.name || `Vehicle ${vehicle.id}`,
    vehicle.status ? `Status: ${vehicle.status}` : null,
    vehicle.license_plate ? `Plate: ${vehicle.license_plate}` : null,
    [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '),
    vehicle.serial_number ? `Serial: ${vehicle.serial_number}` : null,
    vehicle.vin ? `VIN: ${vehicle.vin}` : null,
  ].filter(Boolean).join(' | ');

  const getVehicleSearchText = (vehicle) => [
    vehicle.name,
    vehicle.license_plate,
    vehicle.year,
    vehicle.make,
    vehicle.model,
    vehicle.status,
    vehicle.serial_number,
    vehicle.vin,
  ].filter(Boolean).join(' ').toLowerCase();

  const getAssignableSourcePart = (part) => {
    const sourceParts = Array.isArray(part?.source_parts) && part.source_parts.length ? part.source_parts : [part];
    return sourceParts.find((item) => Number(item?.quantity ?? 0) > 0) || null;
  };

  const openPartDetails = (part) => setDetailPart(part);
  const closePartDetails = () => {
    setDetailPart(null);
    setDetailPhotos([]);
    setDetailPhotosLoading(false);
  };

  useEffect(() => {
    if (!detailPart) return undefined;
    let cancelled = false;
    const partId = detailPart.representative_id || detailPart.id;
    async function loadDetailPhotos() {
      try {
        setDetailPhotosLoading(true);
        const res = await partService.getPhotos(partId);
        const photos = Array.isArray(res.data) ? res.data : [];
        const fallback = detailPart.photo_url;
        const withFallback = fallback && !photos.some((photo) => photo.url === fallback)
          ? [{ id: 'cover', url: fallback, original_name: 'Cover photo' }, ...photos]
          : photos;
        if (!cancelled) setDetailPhotos(withFallback.filter((photo) => photo?.url));
      } catch (error) {
        console.error('Error loading inventory part photos:', error);
        if (!cancelled) setDetailPhotos(detailPart.photo_url ? [{ id: 'cover', url: detailPart.photo_url, original_name: 'Cover photo' }] : []);
      } finally {
        if (!cancelled) setDetailPhotosLoading(false);
      }
    }
    loadDetailPhotos();
    return () => {
      cancelled = true;
    };
  }, [detailPart]);

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    return String(value);
  };

  const detailField = (label, value) => (
    <Grid item xs={12} sm={6} md={4} key={label}>
      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fbff', height: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 800, color: '#001f3f', mt: 0.35, overflowWrap: 'anywhere' }}>
          {formatValue(value)}
        </Typography>
      </Paper>
    </Grid>
  );

  const renderInventoryLocationChips = (part) => (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
      {inventoryLocationLabels(part).map((label) => (
        <Chip
          key={label}
          size="small"
          icon={<Inventory2Icon sx={{ fontSize: '16px !important' }} />}
          label={`Inventory: ${label}`}
          sx={inventoryLocationChipSx(label)}
        />
      ))}
    </Box>
  );

  const openAssignDialog = (part, quantity = 1) => {
    const moveQuantity = Math.max(1, Math.min(2, Number(quantity || 1)));
    if (Number(part.quantity ?? 0) < moveQuantity || !getAssignableSourcePart(part)) {
      setSnackbar({ open: true, severity: 'warning', message: `${partLabel(part)} has no available stock to move.` });
      return;
    }
    setAssignDialog({ open: true, part, vehicleId: vehicles[0]?.id || '', quantity: moveQuantity });
  };

  const closeAssignDialog = () => {
    if (assigningId) return;
    setAssignDialog({ open: false, part: null, vehicleId: '', quantity: 1 });
  };

  const assignUnitsToVehicle = async () => {
    const part = assignDialog.part;
    const vehicleId = assignDialog.vehicleId;
    const moveQuantity = Math.max(1, Math.min(2, Number(assignDialog.quantity || 1)));
    const sourcePart = getAssignableSourcePart(part);
    if (!part || !sourcePart || !vehicleId) {
      setSnackbar({ open: true, severity: 'warning', message: 'Select a part and destination vehicle.' });
      return;
    }
    if (Number(part.quantity ?? 0) < moveQuantity) {
      setSnackbar({ open: true, severity: 'warning', message: `Only ${Number(part.quantity ?? 0).toLocaleString()} available in inventory.` });
      return;
    }

    try {
      setAssigningId(part.id);
      await partService.assignOne(sourcePart.id, vehicleId, moveQuantity);
      await loadParts();
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      const vehicle = vehicles.find((item) => String(item.id) === String(vehicleId));
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Moved ${moveQuantity} unit${moveQuantity === 1 ? '' : 's'} of ${partLabel(part)} to ${vehicle ? (vehicle.name || `Vehicle ${vehicle.id}`) : 'vehicle'}.`,
      });
      setAssignDialog({ open: false, part: null, vehicleId: '', quantity: 1 });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to move part to vehicle.',
      });
    } finally {
      setAssigningId(null);
    }
  };

  const renderQuantityControls = (part, quantity) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, width: '100%', justifyContent: { xs: 'space-between', md: 'center' } }}>
      <Tooltip title="Decrease quantity">
        <IconButton size="small" onClick={() => adjustQuantity(part, -1)} sx={{ border: '1px solid #d8e2ef', borderRadius: 1.5 }}>
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <TextField
        size="small"
        type="number"
        value={quantity}
        onChange={(event) => updateDraft(part.id, 'quantity', numericValue(event.target.value))}
        inputProps={{ min: 0, style: { textAlign: 'center' } }}
        sx={{ width: { xs: '100%', md: 86 }, maxWidth: 120 }}
      />
      <Tooltip title="Increase quantity">
        <IconButton size="small" onClick={() => adjustQuantity(part, 1)} sx={{ border: '1px solid #d8e2ef', borderRadius: 1.5 }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const highlightSx = (part) => {
    const sourceIds = Array.isArray(part.source_part_ids) ? part.source_part_ids : [];
    const isHighlighted = Number(part.id) === highlightPartId
      || Number(part.representative_id) === highlightPartId
      || sourceIds.map(Number).includes(Number(highlightPartId));

    if (!isHighlighted) return {};
    return {
      border: '3px solid #ff9800',
      boxShadow: '0 0 0 4px rgba(255,152,0,0.18), 0 12px 34px rgba(255,152,0,0.20)',
      animation: 'biofleet-inventory-highlight 1.2s ease-in-out 0s 3',
      '@keyframes biofleet-inventory-highlight': {
        '0%, 100%': { backgroundColor: '#fff' },
        '50%': { backgroundColor: '#fff8e1' },
      },
    };
  };

  const renderPartCard = (part) => {
    const draft = drafts[part.id] || {};
    const quantity = numericValue(draft.quantity, part.quantity ?? 0);
    const threshold = numericValue(draft.low_stock_threshold, part.low_stock_threshold ?? 2);
    const emailEnabled = draft.low_stock_email_enabled === true;
    const emailRecipients = draft.low_stock_email_recipients || '';
    const isLow = isPurchaseNeeded(part, threshold);
    const isDirty = quantity !== Number(part.quantity ?? 0)
      || threshold !== Number(part.low_stock_threshold ?? 2)
      || emailEnabled !== (part.low_stock_email_enabled === true)
      || emailRecipients !== (part.low_stock_email_recipients || '');

    return (
      <Paper id={`inventory-part-${part.representative_id || part.id}`} key={part.id} sx={{ p: 2, borderRadius: 3, border: `1px solid ${isLow ? '#ef9a9a' : '#d8e2ef'}`, bgcolor: isLow ? '#fff7f7' : '#fff', ...highlightSx(part) }}>
        <Box onClick={() => openPartDetails(part)} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 1.5, cursor: 'pointer' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, color: '#001f3f', fontSize: '1rem' }}>
              {part.part_type || 'Unnamed Part'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              SKU: {part.sku || '—'}
            </Typography>
          </Box>
          <Chip size="small" label={isLow ? 'Purchase Needed' : 'OK'} color={isLow ? 'error' : 'success'} sx={{ fontWeight: 900 }} />
        </Box>

        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Box onClick={() => openPartDetails(part)} sx={{ p: 1.2, borderRadius: 2, bgcolor: '#f8fafc', cursor: 'pointer' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>OEM / Serial</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>OEM: {part.part_number_oem || '—'}</Typography>
              <Typography variant="caption" color="text.secondary">Serial: {part.serial_number || '—'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box onClick={() => openPartDetails(part)} sx={{ p: 1.2, borderRadius: 2, bgcolor: '#f8fafc', cursor: 'pointer' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Location</Typography>
              <Box sx={{ mt: 0.75 }}>
                {renderInventoryLocationChips(part)}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box onClick={() => openPartDetails(part)} sx={{ p: 1.2, borderRadius: 2, bgcolor: '#f5fdf7', border: '1px solid #22c55e33', cursor: 'pointer' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Shelf Location</Typography>
              <Typography variant="body2" sx={{ fontWeight: 900, color: '#166534', mt: 0.35 }}>
                {shelfLocationText(part)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ fontWeight: 900, color: '#001f3f' }}>Quantity</Typography>
            {renderQuantityControls(part, quantity)}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Reminder Threshold"
              value={threshold}
              onChange={(event) => updateDraft(part.id, 'low_stock_threshold', numericValue(event.target.value, 2))}
              helperText="Purchase reminder appears when quantity is below this"
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2, bgcolor: '#f8fafc' }}>
              <FormControlLabel
                control={<Switch checked={emailEnabled} onChange={(event) => updateDraft(part.id, 'low_stock_email_enabled', event.target.checked)} />}
                label="Email when low"
              />
              <TextField
                fullWidth
                size="small"
                label="Email Recipients"
                value={emailRecipients}
                disabled={!emailEnabled}
                onChange={(event) => updateDraft(part.id, 'low_stock_email_recipients', event.target.value)}
                placeholder="parts@example.com"
              />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant={isDirty ? 'contained' : 'outlined'}
              startIcon={<SaveIcon />}
              disabled={!isDirty || savingId === part.id}
              onClick={() => savePart(part)}
              sx={{ bgcolor: isDirty ? '#001f3f' : undefined, fontWeight: 900 }}
            >
              {savingId === part.id ? 'Saving…' : 'Save Inventory'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              startIcon={<LocalShippingIcon />}
              disabled={quantity <= 0 || assigningId === part.id}
              onClick={() => openAssignDialog(part, 1)}
              sx={{ fontWeight: 900 }}
            >
              Move 1 Unit To Vehicle
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<LocalShippingIcon />}
              disabled={quantity < 2 || assigningId === part.id}
              onClick={() => openAssignDialog(part, 2)}
              sx={{
                fontWeight: 900,
                color: '#fff',
                background: 'linear-gradient(135deg, #7b1fa2 0%, #00a6d6 100%)',
                boxShadow: '0 8px 20px rgba(0, 166, 214, 0.25)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6a1b9a 0%, #008fbd 100%)',
                  boxShadow: '0 10px 24px rgba(123, 31, 162, 0.28)',
                },
                '&.Mui-disabled': {
                  background: '#d7dce4',
                  color: '#7d8794',
                },
              }}
            >
              Move 2 Units
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Box>
      <Card className="portal-page-header" sx={{ borderRadius: 2.25, boxShadow: 2, mb: 1.25 }}>
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 }, '&:last-child': { pb: { xs: 1.15, sm: 1.35, md: 1.55 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: { xs: 1, sm: 1.25 }, flexWrap: 'wrap', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box sx={{ width: { xs: 40, sm: 46 }, height: { xs: 40, sm: 46 }, flex: '0 0 auto', borderRadius: 2, bgcolor: '#001f3f', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Inventory2Icon sx={{ fontSize: { xs: 23, sm: 27 } }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#001f3f', fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05, mb: 0.1 }}>
                  Inventory Management
                </Typography>
                <Typography color="text.secondary" sx={{ fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                  Manage Shelf, Solvent Shed, Shop, and Parts Truck inventory quantities and purchase reminder thresholds.
                </Typography>
              </Box>
            </Box>
            <Button fullWidth={isMobile} size="small" variant="outlined" onClick={() => { window.location.hash = '/parts'; }} sx={{ fontWeight: 900, px: 1.55, py: 0.62, minHeight: 36, borderRadius: 1.6, fontSize: { xs: '0.78rem', sm: '0.86rem' } }}>
              Open Parts Table
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={1} sx={{ mb: 2 }}>
        {[
          { key: 'shelfStock', label: 'Stock Items - On Shelf', value: stats.shelfStockItems, accent: '#3b658b', tint: '#c6dff7', icon: <Inventory2Icon />, helper: 'Show stock items located on shelf only' },
          { key: 'totalInventory', label: 'Total Inventory Qty', value: stats.totalInventoryQuantity, accent: '#356d59', tint: '#c4e8d1', icon: <Inventory2Icon />, helper: 'Show Shelf, Solvent Shed, Shop, and Parts Truck inventory' },
          { key: 'lowShelf', label: 'Low Stock - Shelf', value: stats.lowShelfStock, accent: '#866523', tint: '#f9dfa4', icon: 'LOW', helper: 'Show low-stock shelf parts only' },
          { key: 'outShelf', label: 'Out Of Stock - Shelf', value: stats.outShelfStock, accent: '#87516d', tint: '#f3cde2', icon: 'OUT', helper: 'Show shelf parts with zero stock' },
          { key: 'outPartsTruck', label: 'Out Of Stock - Parts Truck', value: stats.outPartsTruckStock, accent: '#775e8b', tint: '#e4cef1', icon: 'OUT', helper: 'Show parts truck items with zero stock' },
        ].map((item) => (
          <Grid size={{ xs: 6, md: 4, lg: 2.4 }} key={item.key}>
            <SummaryCard {...item} value={item.value.toLocaleString()} title={item.helper}
              selected={inventoryFilter === item.key} onClick={() => setInventoryFilter(item.key)} />
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #d8e2ef' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={`${activeInventoryFilterLabel}: ${filteredParts.length.toLocaleString()} item${filteredParts.length === 1 ? '' : 's'}`}
            color={['outOfStock', 'outShelf', 'outPartsTruck'].includes(inventoryFilter) ? 'error' : ['lowStock', 'lowShelf'].includes(inventoryFilter) ? 'warning' : inventoryFilter === 'all' ? 'default' : 'success'}
            sx={{ fontWeight: 900 }}
          />
          {!['all', 'totalInventory'].includes(inventoryFilter) && (
            <Button size="small" onClick={() => setInventoryFilter('all')} sx={{ fontWeight: 900 }}>
              Clear Filter
            </Button>
          )}
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by SKU, part type, OEM, serial number, location..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <Paper sx={{ py: 6, textAlign: 'center', borderRadius: 3 }}>
          <CircularProgress />
        </Paper>
      ) : filteredParts.length === 0 ? (
        <Paper sx={{ py: 6, px: 2, textAlign: 'center', borderRadius: 3, color: 'text.secondary' }}>
          No Shelf, Solvent Shed, Shop, or Parts Truck stock found.
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {paginatedParts.map(renderPartCard)}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2, overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1260 }}>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#001f3f', color: 'white', fontWeight: 900 } }}>
                <TableCell>Part</TableCell>
                <TableCell>OEM / Serial</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Shelf Location</TableCell>
                <TableCell align="center">Quantity</TableCell>
                <TableCell align="center">Reminder Threshold</TableCell>
                <TableCell>Email Reminder</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedParts.map((part) => {
                const draft = drafts[part.id] || {};
                const quantity = numericValue(draft.quantity, part.quantity ?? 0);
                const threshold = numericValue(draft.low_stock_threshold, part.low_stock_threshold ?? 2);
                const emailEnabled = draft.low_stock_email_enabled === true;
                const emailRecipients = draft.low_stock_email_recipients || '';
                const isLow = isPurchaseNeeded(part, threshold);
                const isDirty = quantity !== Number(part.quantity ?? 0)
                  || threshold !== Number(part.low_stock_threshold ?? 2)
                  || emailEnabled !== (part.low_stock_email_enabled === true)
                  || emailRecipients !== (part.low_stock_email_recipients || '');
                return (
                  <TableRow id={`inventory-part-${part.representative_id || part.id}`} key={part.id} hover sx={highlightSx(part)}>
                    <TableCell onClick={() => openPartDetails(part)} sx={{ cursor: 'pointer' }}>
                      <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>{part.part_type || 'Unnamed Part'}</Typography>
                      <Typography variant="caption" color="text.secondary">SKU: {part.sku || '—'} • {part.make || ''} {part.model || ''}</Typography>
                    </TableCell>
                    <TableCell onClick={() => openPartDetails(part)} sx={{ cursor: 'pointer' }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>OEM: {part.part_number_oem || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">Serial: {part.serial_number || '—'}</Typography>
                    </TableCell>
                    <TableCell onClick={() => openPartDetails(part)} sx={{ cursor: 'pointer', minWidth: 220 }}>
                      {renderInventoryLocationChips(part)}
                    </TableCell>
                    <TableCell onClick={() => openPartDetails(part)} sx={{ cursor: 'pointer', minWidth: 140 }}>
                      <Chip
                        size="small"
                        label={shelfLocationText(part)}
                        sx={{
                          bgcolor: '#e5f9eb',
                          color: '#166534',
                          border: '1px solid #22c55e55',
                          fontWeight: 900,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">{renderQuantityControls(part, quantity)}</TableCell>
                    <TableCell align="center">
                      <TextField
                        size="small"
                        type="number"
                        value={threshold}
                        onChange={(event) => updateDraft(part.id, 'low_stock_threshold', numericValue(event.target.value, 2))}
                        helperText="Purchase reminder below this"
                        inputProps={{ min: 0, style: { textAlign: 'center' } }}
                        sx={{ width: 140 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 230 }}>
                        <FormControlLabel
                          control={<Switch size="small" checked={emailEnabled} onChange={(event) => updateDraft(part.id, 'low_stock_email_enabled', event.target.checked)} />}
                          label="Email"
                        />
                        <TextField
                          fullWidth
                          size="small"
                          value={emailRecipients}
                          disabled={!emailEnabled}
                          onChange={(event) => updateDraft(part.id, 'low_stock_email_recipients', event.target.value)}
                          placeholder="parts@example.com"
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={isLow ? 'Purchase Needed' : 'OK'} color={isLow ? 'error' : 'success'} sx={{ fontWeight: 900 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant={isDirty ? 'contained' : 'outlined'}
                          startIcon={<SaveIcon />}
                          disabled={!isDirty || savingId === part.id}
                          onClick={() => savePart(part)}
                          sx={{ bgcolor: isDirty ? '#001f3f' : undefined, fontWeight: 900 }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          startIcon={<LocalShippingIcon />}
                          disabled={quantity <= 0 || assigningId === part.id}
                          onClick={() => openAssignDialog(part, 1)}
                          sx={{ fontWeight: 900, whiteSpace: 'nowrap' }}
                        >
                          Move 1
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<LocalShippingIcon />}
                          disabled={quantity < 2 || assigningId === part.id}
                          onClick={() => openAssignDialog(part, 2)}
                          sx={{
                            fontWeight: 900,
                            whiteSpace: 'nowrap',
                            color: '#fff',
                            background: 'linear-gradient(135deg, #7b1fa2 0%, #00a6d6 100%)',
                            boxShadow: '0 7px 18px rgba(0, 166, 214, 0.22)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #6a1b9a 0%, #008fbd 100%)',
                              boxShadow: '0 9px 22px rgba(123, 31, 162, 0.26)',
                            },
                            '&.Mui-disabled': {
                              background: '#d7dce4',
                              color: '#7d8794',
                            },
                          }}
                        >
                          Move 2
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && filteredParts.length > 0 && (
        <TablePagination
          component={Paper}
          count={filteredParts.length}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 150]}
          sx={{ mt: 1, borderRadius: 2 }}
        />
      )}

      <Dialog open={assignDialog.open} onClose={closeAssignDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          Move {assignDialog.quantity} Unit{assignDialog.quantity === 1 ? '' : 's'} To Vehicle
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            This moves exactly {assignDialog.quantity} unit{assignDialog.quantity === 1 ? '' : 's'} from Shelf, Solvent Shed, Shop, or Parts Truck inventory, creates vehicle part record{assignDialog.quantity === 1 ? '' : 's'}, and tracks the movement.
          </Alert>
          <Typography sx={{ fontWeight: 900, color: '#001f3f', mb: 0.5 }}>
            {assignDialog.part ? partLabel(assignDialog.part) : 'Part'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Available inventory quantity: {Number(assignDialog.part?.quantity ?? 0).toLocaleString()}
          </Typography>
          <Autocomplete
            fullWidth
            size="small"
            options={vehicles}
            value={vehicles.find((vehicle) => String(vehicle.id) === String(assignDialog.vehicleId)) || null}
            getOptionLabel={getVehicleLabel}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            filterOptions={(options, state) => {
              const searchValue = state.inputValue.trim().toLowerCase();
              if (!searchValue) return options;
              return options.filter((vehicle) => getVehicleSearchText(vehicle).includes(searchValue));
            }}
            onChange={(_event, vehicle) => setAssignDialog((prev) => ({ ...prev, vehicleId: vehicle?.id || '' }))}
            noOptionsText="No matching vehicles"
            renderOption={(props, vehicle) => (
              <Box component="li" {...props} key={vehicle.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                <Typography sx={{ fontWeight: 900, color: '#001f3f', lineHeight: 1.2 }}>
                  {vehicle.name || `Vehicle ${vehicle.id}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                  {[
                    [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '),
                    vehicle.license_plate ? `Plate: ${vehicle.license_plate}` : null,
                    vehicle.serial_number ? `Serial: ${vehicle.serial_number}` : null,
                    vehicle.vin ? `VIN: ${vehicle.vin}` : null,
                  ].filter(Boolean).join(' • ')}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search destination vehicle"
                placeholder="Type truck name, plate, serial, VIN..."
                helperText="Search by name, make/model, plate, serial number, or VIN."
              />
            )}
          />
          {vehicles.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No non-archived vehicles are available for assignment.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeAssignDialog} disabled={Boolean(assigningId)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<LocalShippingIcon />}
            disabled={!assignDialog.vehicleId || Boolean(assigningId)}
            onClick={assignUnitsToVehicle}
            sx={{ bgcolor: '#001f3f', fontWeight: 900 }}
          >
            {assigningId ? 'Moving...' : `Move ${assignDialog.quantity} Unit${assignDialog.quantity === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(detailPart)} onClose={closePartDetails} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f', pb: 1 }}>
          Inventory Part Details
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 700 }}>
            {detailPart ? partLabel(detailPart) : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detailPart && (
            <Box>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#f8fbff', mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#001f3f' }}>
                    Part Photo
                  </Typography>
                  {detailPhotosLoading && <CircularProgress size={18} />}
                </Box>
                {detailPhotos.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
                    {detailPhotos.map((photo, index) => (
                      <Box
                        key={photo.id || `${photo.url}-${index}`}
                        component="a"
                        href={photo.url}
                        target="_blank"
                        rel="noreferrer"
                        sx={{
                          display: 'block',
                          width: { xs: 170, sm: 220 },
                          height: { xs: 135, sm: 170 },
                          flex: '0 0 auto',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid #d8e2ef',
                          bgcolor: '#fff',
                          boxShadow: '0 6px 18px rgba(0,31,63,0.10)',
                        }}
                      >
                        <Box
                          component="img"
                          src={photo.url}
                          alt={photo.original_name || `Part photo ${index + 1}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ minHeight: 135, borderRadius: 2, border: '1px dashed #b8c7d9', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontWeight: 800 }}>
                    No part photo uploaded.
                  </Box>
                )}
                {detailPhotos.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Click a photo to open it full size.
                  </Typography>
                )}
              </Paper>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip label={`Quantity: ${Number(detailPart.quantity ?? 0).toLocaleString()}`} color={isPurchaseNeeded(detailPart, detailPart.low_stock_threshold ?? 2) ? 'error' : 'success'} sx={{ fontWeight: 900 }} />
                <Chip label={`Threshold: ${Number(detailPart.low_stock_threshold ?? 2).toLocaleString()}`} variant="outlined" sx={{ fontWeight: 900 }} />
                <Chip label={detailPart.low_stock_email_enabled ? 'Email Reminder On' : 'Email Reminder Off'} variant="outlined" sx={{ fontWeight: 900 }} />
                <Chip label={`Sources: ${Array.isArray(detailPart.source_parts) ? detailPart.source_parts.length : 1}`} variant="outlined" sx={{ fontWeight: 900 }} />
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#001f3f', mb: 1 }}>
                Part Information
              </Typography>
              <Grid container spacing={1.25} sx={{ mb: 2 }}>
                {detailField('Part Type', detailPart.part_type)}
                {detailField('SKU', detailPart.sku)}
                {detailField('BioMed SKU', detailPart.biomed_part_number_sku)}
                {detailField('OEM Part Number', detailPart.part_number_oem)}
                {detailField('Serial Number', detailPart.serial_number)}
                {detailField('Cross Reference', detailPart.cross_reference)}
                {detailField('Make', detailPart.make)}
                {detailField('Model', detailPart.model)}
                {detailField('Year', detailPart.year)}
                {detailField('New / Used', detailPart.used_new === false ? 'Used' : 'New')}
                {detailField('Condition Scale', detailPart.condition_scale)}
                {detailField('Location', detailPart.location)}
                {detailField('Source Locations', detailPart.source_locations)}
                {detailField('Source Part IDs', detailPart.source_part_ids)}
                {detailField('Representative ID', detailPart.representative_id || detailPart.id)}
                {detailField('Date Purchased', detailPart.date_purchased ? formatPortalDate(detailPart.date_purchased) : '')}
                {detailField('Purchase Price', detailPart.purchase_price ? `$${detailPart.purchase_price}` : '')}
                {detailField('Current Value', detailPart.current_value ? `$${detailPart.current_value}` : '')}
                {detailField('Receipt Number', detailPart.receipt_number)}
                {detailField('Low Stock Emails', detailPart.low_stock_email_recipients)}
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#001f3f', mb: 1 }}>
                Notes & Storage
              </Typography>
              <Grid container spacing={1.25} sx={{ mb: 2 }}>
                {detailField('Notes', detailPart.notes)}
                {detailField('Condition Notes', detailPart.condition_notes)}
                {detailField('Storage Condition', detailPart.condition_of_storage)}
                {detailField('Recommended Storage', detailPart.recommended_storage)}
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#001f3f', mb: 1 }}>
                Source Inventory Records
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#f4f7fb', fontWeight: 900, color: '#001f3f' } }}>
                      <TableCell>ID</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell>Serial</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(Array.isArray(detailPart.source_parts) && detailPart.source_parts.length ? detailPart.source_parts : [detailPart]).map((source) => (
                      <TableRow key={source.id}>
                        <TableCell>{source.id}</TableCell>
                        <TableCell>{source.sku || '—'}</TableCell>
                        <TableCell>{source.location || '—'}</TableCell>
                        <TableCell align="center">{Number(source.quantity ?? 0).toLocaleString()}</TableCell>
                        <TableCell>{source.serial_number || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closePartDetails}>Close</Button>
          {detailPart && (
            <>
              <Button
                variant="contained"
                onClick={() => {
                  closePartDetails();
                  openAssignDialog(detailPart, 1);
                }}
                disabled={Number(detailPart.quantity ?? 0) <= 0}
                sx={{ bgcolor: '#001f3f', fontWeight: 900 }}
              >
                Move 1 Unit To Vehicle
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  closePartDetails();
                  openAssignDialog(detailPart, 2);
                }}
                disabled={Number(detailPart.quantity ?? 0) < 2}
                sx={{
                  fontWeight: 900,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #7b1fa2 0%, #00a6d6 100%)',
                  boxShadow: '0 8px 20px rgba(0, 166, 214, 0.24)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #6a1b9a 0%, #008fbd 100%)',
                    boxShadow: '0 10px 24px rgba(123, 31, 162, 0.28)',
                  },
                  '&.Mui-disabled': {
                    background: '#d7dce4',
                    color: '#7d8794',
                  },
                }}
              >
                Move 2 Units
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
